<template>
  <div class="d-flex flex-column gap-4 mt-4">
    <!-- Loading State: Shimmer Table -->
    <div v-if="loading">
        <lb-datatablenew
            key="shimmer-table"
            :headers="headers"
            :items="[{id:1},{id:2},{id:3},{id:4},{id:5}]"
            :enableslot="headers.map(h => h.value)"
            class="elevation-0"
            :hide-default-footer="true"
            :loading="false"
        >
            <template v-for="header in headers" v-slot:[header.value]>
                <v-skeleton-loader :key="header.value" type="text" class="my-2"></v-skeleton-loader>
            </template>
        </lb-datatablenew>
    </div>

    <div v-else class="d-flex flex-column gap-4">
        <div v-if="selectedData.length > 0" class="d-flex align-center justify-end gap-4 revoke-access-tab__top-bar ml-auto">
            <h5 class="font-weight-medium">{{ selectedData.length }} Selected</h5>
            <base-button rejectFilled :disabled="!isPermissionEnabled" @click="promptRevoke(selectedData)" data-testid="revoke-access-tab__top-bulk-revoke-btn" label="Revoke Access" />
        </div>

        <lb-datatablenew
            key="data-table"
            data-testid="revoke-access-tab__datatable"
            :headers="headers"
            :items="displayedItems"
            :loading="false"
            :enableslot="['indexno', 'code', 'action']"
            :multiselectkey="isPermissionEnabled ? '_id' : ''"
            :selecteditems="selectedData.map(x => x._id)"
            @selectItem="handleSelection"
            class="elevation-0"
            :hide-default-footer="true"
        >
            <template v-slot:indexno="{ item }">
                <div class="text-center">
                    {{ (page - 1) * itemsPerPage + displayedItems.indexOf(item) + 1 }}
                </div>
            </template>
            <template v-slot:code="{ item }">
                <div data-testid="revoke-access-tab__code-name">
                    <span class="">{{ item.code }}</span> - <span>{{ item.name }}</span>
                </div>
            </template>
            <template v-slot:action="{ item }">
                <div class="d-flex justify-center">
                    <v-tooltip bottom content-class="tooltip-bottom">
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <base-button image="/img/icons/RevokeIcon.svg" icon-style small :disabled="!isPermissionEnabled" @click="promptRevoke([item])" data-testid="revoke-access-tab__revoke-btn" />
                            </span>
                        </template>
                        <span>{{ isPermissionEnabled ? 'Revoke Access' : 'Feature Disabled' }}</span>
                    </v-tooltip>
                </div>
            </template>
        </lb-datatablenew>

        <Pagination
            v-if="items.length > 0"
            :list="items"
            :itemsperpage="itemsPerPage"
            :value="page"
            :lengthOverride="pageCountVal"
            @input="handlePageChange"
            @paginated-data="handlePaginatedData"
            class="mt-4"
        />
    </div>

    <UserPermissionConfirmationDialog
        v-if="confirmDialog"
        v-model="confirmDialog"
        title="Revoke Access"
        image="/img/icons/AlertIcon.svg"
        message="Are you sure you want to revoke access for the following items?"
        :items="itemsToRevoke"
        type="revoke"
        @confirm="confirmRevoke"
    />
  </div>
</template>

<script>
import UserPermissionConfirmationDialog from './UserPermissionConfirmationDialog.vue';
import Pagination from '../../../../../views/common/Pagination.vue';

export default {
    name: 'RevokeAccessTab',
    components: {
        UserPermissionConfirmationDialog,
        Pagination
    },
    props: {
        items: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        totalRecords: {
            type: Number,
            default: 0
        },
        serverSide: {
            type: Boolean,
            default: false
        },
        currentPage: {
            type: Number,
            default: 1
        },
        isPermissionEnabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedData: [],
            confirmDialog: false,
            itemsToRevoke: [],
            page: this.currentPage,
            itemsPerPage: 100,
            paginatedItems: []
        }
    },
    watch: {
        currentPage(val) {
            this.page = val;
        }
    },
    methods: {
        handleSelection(ids) {
            // Note: Since we only show minimal items, selection from invisible pages is hard with this component
            // We should filter from the FULL list if ids are preserved.
            // lb-datatablenew emits selected array of IDs, so we can check against full items.
            this.selectedData = this.items.filter(item => ids.indexOf(item._id) !== -1);
        },
        promptRevoke(items) {
            if (!this.isPermissionEnabled) return;
            this.itemsToRevoke = items;
            this.confirmDialog = true;
        },
        confirmRevoke() {
            this.$emit('revoke', this.itemsToRevoke);
            this.confirmDialog = false;
            this.selectedData = [];
            this.itemsToRevoke = [];
        },
        handlePageChange(newPage) {
            this.page = newPage;
            if (this.serverSide) {
                this.$emit('page-change', newPage);
            }
        },
        handlePaginatedData(paginationInfo) {
            if (!this.serverSide) {
                this.paginatedItems = paginationInfo.data;
            }
        }
    },
    computed: {
        displayedItems() {
             return this.serverSide ? this.items : this.paginatedItems;
        },
        pageCountVal() {
            if (this.serverSide && this.totalRecords > 0) {
                 return Math.ceil(this.totalRecords / this.itemsPerPage);
            }
            return 0; // Let Pagination calculate
        }, 
        headers() {
            const headers = [
                {
                    text: 'S.No',
                    value: 'indexno',
                    sortable: false,
                    width: '60px',
                    align: 'center'
                },
                {
                    text: 'RU Code & Description',
                    value: 'code',
                    sortable: false
                },
                {
                    text: 'Network',
                    value: 'network',
                    sortable: false
                },
                {
                    text: 'Region',
                    value: 'region',
                    sortable: false
                },
                {
                    text: 'Country',
                    value: 'country',
                    sortable: false
                }
            ];

            if (this.isPermissionEnabled) {
                headers.push({
                    text: 'Actions',
                    value: 'action',
                    sortable: false,
                    align: 'center',
                    width: '100px'
                });
            }
            
            return headers;
        }
    }
}
</script>

<style scoped>
.revoke-access-tab__top-bar {
    border: 1px solid var(--red-500);
    padding: 8px 12px;
    border-radius: 8px;
    width: fit-content;
    color: var(--red-500);
    background: var(--red-100);
}
</style>